import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { formatEUR, formatDateFR, FrenchTaxCalculator, type TaxSummary } from '../../services/frenchTaxCalculator';

interface TaxCalculationResultProps {
  summary: TaxSummary | null;
  year: number;
  onRecalculate: () => void;
}

export function TaxCalculationResult({ summary, year, onRecalculate }: TaxCalculationResultProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [marginalRate, setMarginalRate] = useState(0.30); // Default 30% TMI
  // V2.E.UI.3 — collapsible breakdown of the PFU vs progressive
  // comparison. Hidden by default to keep the comparison panel tight
  // for users who don't have rewards classified.
  const [showBreakdown, setShowBreakdown] = useState(false);

  if (!summary) {
    return (
      <div style={{
        padding: '48px',
        textAlign: 'center',
      }}>
        <Icons.Calculator size={48} style={{ color: 'var(--muted)', marginBottom: '16px' }} />
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
          {t('taxCenter.calculation.noData', 'No Calculation Available')}
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '20px' }}>
          {t('taxCenter.calculation.clickCalculate', 'Click the button to calculate your taxes')}
        </p>
        <button onClick={onRecalculate} className="btn-primary" style={{ padding: '12px 24px' }}>
          <Icons.Calculator size={18} style={{ marginRight: '8px' }} />
          {t('taxCenter.calculate', 'Calculate Taxes')}
        </button>
      </div>
    );
  }

  // Compare tax regimes
  const calculator = new FrenchTaxCalculator([]);
  const comparison = calculator.compareTaxRegimes(summary, marginalRate);

  return (
    <div>
      {/* Main Summary Card */}
      <div style={{
        padding: '24px',
        background: summary.isExempt
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
        borderRadius: '16px',
        marginBottom: '24px',
        border: summary.isExempt ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(59, 130, 246, 0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
          <div>
            <h2 style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '8px' }}>
              {t('taxCenter.calculation.taxDue', 'Tax Due')} {year}
            </h2>
            <div style={{
              fontSize: '36px',
              fontWeight: 700,
              color: summary.isExempt ? '#10b981' : 'var(--text)',
            }}>
              {summary.isExempt ? '0 €' : formatEUR(summary.flatTax)}
            </div>
            {summary.isExempt && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '8px',
                color: '#10b981',
              }}>
                <Icons.Check size={20} />
                <span style={{ fontSize: '14px', fontWeight: 500 }}>
                  {t('taxCenter.calculation.exemptMessage', 'Exempt - Total sales below €305 threshold')}
                </span>
              </div>
            )}
          </div>

          <button onClick={onRecalculate} className="btn-ghost" style={{ padding: '8px 16px' }}>
            <Icons.RefreshCw size={16} style={{ marginRight: '6px' }} />
            {t('taxCenter.calculation.recalculate', 'Recalculate')}
          </button>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px',
        marginBottom: '24px',
      }}>
        {/* Capital Gains */}
        <div style={{
          padding: '20px',
          background: 'var(--hover)',
          borderRadius: '12px',
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--muted)', marginBottom: '16px' }}>
            {t('taxCenter.calculation.capitalGains', 'Capital Gains / Losses')}
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                {t('taxCenter.calculation.totalSales', 'Total Sales')}
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {summary.totalSales}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                {t('taxCenter.calculation.totalSaleValue', 'Total Sale Value')}
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {formatEUR(summary.totalSaleValue)}
              </span>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: '#10b981' }}>
                {t('taxCenter.calculation.gains', 'Gains')}
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                +{formatEUR(summary.totalCapitalGains)}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: '#ef4444' }}>
                {t('taxCenter.calculation.losses', 'Losses')}
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#ef4444' }}>
                -{formatEUR(summary.totalCapitalLosses)}
              </span>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {t('taxCenter.calculation.netGain', 'Net Capital Gain')}
              </span>
              <span style={{
                fontSize: '16px',
                fontWeight: 700,
                color: summary.netCapitalGain >= 0 ? '#10b981' : '#ef4444',
              }}>
                {formatEUR(summary.netCapitalGain)}
              </span>
            </div>
          </div>
        </div>

        {/* Tax Breakdown */}
        <div style={{
          padding: '20px',
          background: 'var(--hover)',
          borderRadius: '12px',
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--muted)', marginBottom: '16px' }}>
            {t('taxCenter.calculation.taxBreakdown', 'Tax Breakdown (PFU 30%)')}
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                {t('taxCenter.calculation.taxableAmount', 'Taxable Amount')}
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {formatEUR(summary.taxableAmount)}
              </span>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                {t('taxCenter.calculation.incomeTax', 'Income Tax')} (12.8%)
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {formatEUR(summary.incomeTaxPortion)}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                {t('taxCenter.calculation.socialContributions', 'Social Contributions')} (17.2%)
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {formatEUR(summary.socialContributions)}
              </span>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {t('taxCenter.calculation.totalTax', 'Total Tax')} (30%)
              </span>
              <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--accent)' }}>
                {formatEUR(summary.flatTax)}
              </span>
            </div>
          </div>
        </div>

        {/* Tax Regime Comparison */}
        <div style={{
          padding: '20px',
          background: 'var(--hover)',
          borderRadius: '12px',
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--muted)', marginBottom: '16px' }}>
            {t('taxCenter.calculation.regimeComparison', 'PFU vs Progressive Tax')}
          </h3>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
              {t('taxCenter.calculation.marginalRate', 'Your Marginal Tax Rate (TMI)')}
            </label>
            <select
              aria-label={t('taxCenter.calculation.marginalRate', 'Your Marginal Tax Rate (TMI)')}
              value={marginalRate}
              onChange={(e) => setMarginalRate(parseFloat(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                background: 'var(--card)',
                color: 'var(--text)',
                fontSize: '14px',
              }}
            >
              <option value="0">0%</option>
              <option value="0.11">11%</option>
              <option value="0.30">30%</option>
              <option value="0.41">41%</option>
              <option value="0.45">45%</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 12px',
              background: comparison.recommendation === 'pfu' ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
              borderRadius: '8px',
            }}>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                PFU (30%)
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {formatEUR(comparison.pfuAmount)}
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 12px',
              background: comparison.recommendation === 'progressive' ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
              borderRadius: '8px',
            }}>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                {t('taxCenter.calculation.progressive', 'Progressive')} ({Math.round(marginalRate * 100)}% + 17.2%)
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {formatEUR(comparison.progressiveAmount)}
              </span>
            </div>

            <div style={{
              padding: '12px',
              background: 'rgba(16, 185, 129, 0.1)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Icons.Lightbulb size={18} style={{ color: '#10b981' }} />
              <span style={{ fontSize: '13px', color: '#10b981' }}>
                {t('taxCenter.calculation.recommendation', 'Recommended')}:{' '}
                <strong>{comparison.recommendation === 'pfu' ? 'PFU' : t('taxCenter.calculation.progressive', 'Progressive')}</strong>
                {comparison.savings > 0 && ` (${t('taxCenter.calculation.save', 'save')} ${formatEUR(comparison.savings)})`}
              </span>
            </div>

            {/* V2.E.UI.3 — collapsible breakdown of the comparison
                (V2.E3 added the structured `breakdown` field). Only
                offered when at least one rewards bucket is non-zero,
                otherwise the simple 2-row comparison above is enough. */}
            {(comparison.breakdown.rewardsRCM.pfu > 0
              || comparison.breakdown.rewardsRCM.progressive > 0
              || comparison.breakdown.rewardsBNC.progressive > 0) && (
              <>
                <button
                  type="button"
                  onClick={() => setShowBreakdown(!showBreakdown)}
                  aria-expanded={showBreakdown}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: 'transparent',
                    border: '1px dashed var(--border)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: 'var(--muted)',
                    fontSize: '12px',
                  }}
                >
                  <span>
                    {showBreakdown
                      ? t('taxCenter.calculation.breakdown.hide', 'Masquer le détail')
                      : t('taxCenter.calculation.breakdown.show', 'Voir le détail par catégorie')}
                  </span>
                  {showBreakdown ? <Icons.ChevronUp size={14} /> : <Icons.ChevronDown size={14} />}
                </button>

                {showBreakdown && (
                  <div
                    style={{
                      marginTop: '4px',
                      padding: '12px',
                      background: 'var(--card)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      overflowX: 'auto',
                    }}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '12px',
                      }}
                    >
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <th
                            style={{
                              padding: '6px 8px',
                              textAlign: 'left',
                              color: 'var(--muted)',
                              fontWeight: 500,
                            }}
                          >
                            {t('taxCenter.calculation.breakdown.category', 'Catégorie')}
                          </th>
                          <th
                            style={{
                              padding: '6px 8px',
                              textAlign: 'right',
                              color: 'var(--muted)',
                              fontWeight: 500,
                            }}
                          >
                            PFU
                          </th>
                          <th
                            style={{
                              padding: '6px 8px',
                              textAlign: 'right',
                              color: 'var(--muted)',
                              fontWeight: 500,
                            }}
                          >
                            {t('taxCenter.calculation.progressive', 'Progressive')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 8px', color: 'var(--text)' }}>
                            {t(
                              'taxCenter.calculation.breakdown.capitalGains',
                              'Plus-values',
                            )}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                            {formatEUR(comparison.breakdown.capitalGains.pfu)}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                            {formatEUR(comparison.breakdown.capitalGains.progressive)}
                          </td>
                        </tr>
                        {(comparison.breakdown.rewardsRCM.pfu > 0
                          || comparison.breakdown.rewardsRCM.progressive > 0) && (
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '6px 8px', color: 'var(--text)' }}>
                              {t(
                                'taxCenter.calculation.breakdown.rewardsRCM',
                                'Rewards RCM',
                              )}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                              {formatEUR(comparison.breakdown.rewardsRCM.pfu)}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                              {formatEUR(comparison.breakdown.rewardsRCM.progressive)}
                            </td>
                          </tr>
                        )}
                        {comparison.breakdown.rewardsBNC.progressive > 0 && (
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '6px 8px', color: 'var(--text)' }}>
                              {t(
                                'taxCenter.calculation.breakdown.rewardsBNC',
                                'Rewards BNC',
                              )}
                              <span style={{ color: 'var(--muted)', fontSize: '11px', marginLeft: '4px' }}>
                                {t(
                                  'taxCenter.calculation.breakdown.bncBothColumns',
                                  '(idem dans les 2 options)',
                                )}
                              </span>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                              {formatEUR(comparison.breakdown.rewardsBNC.progressive)}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                              {formatEUR(comparison.breakdown.rewardsBNC.progressive)}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ padding: '8px', color: 'var(--text)', fontWeight: 600 }}>
                            {t('taxCenter.calculation.breakdown.total', 'Total')}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', color: 'var(--accent)', fontWeight: 700 }}>
                            {formatEUR(comparison.pfuAmount)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', color: 'var(--accent)', fontWeight: 700 }}>
                            {formatEUR(comparison.progressiveAmount)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* V2.E.UI.3 — Loss carry-forward (Art. 150-0 D 12-bis CGI).
          Only rendered when the user declared previous losses
          (V2.E.UI.2) and at least some are eligible. */}
      {summary.previousLossCarryForwardEligible > 0 && (
        <div
          style={{
            padding: '20px',
            background: 'var(--hover)',
            borderRadius: '12px',
            marginBottom: '24px',
          }}
        >
          <h3
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--muted)',
              marginBottom: '16px',
            }}
          >
            {t(
              'taxCenter.calculation.carryForward.title',
              'Moins-values reportées (Art. 150-0 D 12-bis CGI)',
            )}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                {t(
                  'taxCenter.calculation.carryForward.eligible',
                  'Pertes éligibles (10 ans glissants)',
                )}
              </span>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                {formatEUR(summary.previousLossCarryForwardEligible)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                {t(
                  'taxCenter.calculation.carryForward.used',
                  'Appliquées sur cette année',
                )}
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                −{formatEUR(summary.previousLossCarryForwardUsed)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                {t(
                  'taxCenter.calculation.carryForward.remaining',
                  'Reportable les années suivantes',
                )}
              </span>
              <span style={{ fontSize: '14px', color: 'var(--text)' }}>
                {formatEUR(summary.previousLossCarryForwardRemaining)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Passive Income */}
      {(summary.stakingRewards > 0 || summary.miningRewards > 0 || summary.airdrops > 0) && (
        <div style={{
          padding: '20px',
          background: 'var(--hover)',
          borderRadius: '12px',
          marginBottom: '24px',
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--muted)', marginBottom: '16px' }}>
            {t('taxCenter.calculation.passiveIncome', 'Passive Income (BNC regime if regular activity)')}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            {summary.stakingRewards > 0 && (
              <div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                  {t('taxCenter.calculation.stakingRewards', 'Staking Rewards')}
                </div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#8b5cf6' }}>
                  {formatEUR(summary.stakingRewards)}
                </div>
              </div>
            )}
            {summary.miningRewards > 0 && (
              <div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                  {t('taxCenter.calculation.miningRewards', 'Mining Rewards')}
                </div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#f59e0b' }}>
                  {formatEUR(summary.miningRewards)}
                </div>
              </div>
            )}
            {summary.airdrops > 0 && (
              <div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                  {t('taxCenter.calculation.airdrops', 'Airdrops')}
                </div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: '#3b82f6' }}>
                  {formatEUR(summary.airdrops)}
                </div>
              </div>
            )}
          </div>

          {/* V2.E.UI.3 — income-tax breakdown when the user opted in
              via incomeRegime (V2.E1b/V2.E.UI.1). Hidden when nothing
              is classified, since the calculator stays neutral. */}
          {(summary.rewardsIncomeRCM > 0 || summary.rewardsIncomeBNC > 0) && (
            <div
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 500 }}>
                {t(
                  'taxCenter.calculation.rewardsIncomeTax',
                  'Imposition des revenus rewards',
                )}
              </div>
              {summary.rewardsIncomeRCM > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text)' }}>
                    {t(
                      'taxCenter.calculation.rewardsRCM',
                      'RCM (PFU 30 %) sur',
                    )}{' '}
                    {formatEUR(summary.rewardsIncomeRCM)}
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--accent)' }}>
                    {formatEUR(summary.pfuOnRewardsIncome)}
                  </span>
                </div>
              )}
              {summary.rewardsIncomeBNC > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text)' }}>
                    {t(
                      'taxCenter.calculation.rewardsBNC',
                      'BNC (barème) sur',
                    )}{' '}
                    {formatEUR(summary.rewardsIncomeBNC)}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--muted)', fontStyle: 'italic' }}>
                    {t(
                      'taxCenter.calculation.rewardsBNCNote',
                      '× votre taux marginal IR',
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Transaction Details */}
      <div style={{
        padding: '20px',
        background: 'var(--hover)',
        borderRadius: '12px',
      }}>
        <button
          onClick={() => setShowDetails(!showDetails)}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
            {t('taxCenter.calculation.saleDetails', 'Sale Details')} ({summary.sales.length})
          </h3>
          {showDetails ? <Icons.ChevronUp size={20} /> : <Icons.ChevronDown size={20} />}
        </button>

        {showDetails && summary.sales.length > 0 && (
          <div style={{ marginTop: '16px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '8px', textAlign: 'left', color: 'var(--muted)' }}>Date</th>
                  <th style={{ padding: '8px', textAlign: 'left', color: 'var(--muted)' }}>Asset</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: 'var(--muted)' }}>Sale Price</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: 'var(--muted)' }}>Acq. Price</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: 'var(--muted)' }}>Gain/Loss</th>
                </tr>
              </thead>
              <tbody>
                {summary.sales.map((sale, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px', color: 'var(--text)' }}>{formatDateFR(sale.event.date)}</td>
                    <td style={{ padding: '8px', color: 'var(--text)', fontWeight: 500 }}>{sale.event.asset}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: 'var(--text)' }}>{formatEUR(sale.salePrice)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: 'var(--muted)' }}>{formatEUR(sale.acquisitionPrice)}</td>
                    <td style={{
                      padding: '8px',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: sale.capitalGain >= 0 ? '#10b981' : '#ef4444',
                    }}>
                      {sale.capitalGain >= 0 ? '+' : ''}{formatEUR(sale.capitalGain)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
